/// <amd-module name="MarkdownEditor/Components/MarkdownTextArea" />
import { IMarkdownTextAreaProps } from "MarkdownEditor/Components/MarkdownTextArea.Props";
import * as React from "react";

/**
 * This is the text area control that we typically use for discussion
 * It supports mentions and automatically grows as the user types until maximumHeight is reached
 */
export class MarkdownTextArea extends React.PureComponent<IMarkdownTextAreaProps> {
    public static defaultProps: Pick<IMarkdownTextAreaProps, "minHeight" | "maxHeight"> = {
        minHeight: 65,
        maxHeight: 485
    };

    private _textArea: HTMLTextAreaElement;
    private _textAreaMirror: HTMLTextAreaElement;

    public render(): JSX.Element {
        return (
            <div className="md-ta-container">
                <textarea
                    placeholder={this.props.placeholder}
                    onFocus={this.props.onFocus}
                    onBlur={this.props.onBlur}
                    className={"markdown-textarea"}
                    value={this.props.value}
                    ref={this._setTextArea}
                    onChange={this._onTextChange}
                    onPaste={this._onPaste}
                    autoFocus={this.props.autoFocus}
                    onKeyDown={this.props.onKeyDown}
                    disabled={this.props.disabled}
                    aria-label={this.props.ariaLabel}
                    aria-labelledby={this.props.ariaLabelledBy}
                />
                <textarea
                    value={this.props.value}
                    aria-hidden={"true"}
                    readOnly={true}
                    className={"markdown-textarea-mirror"}
                    ref={this._setTextAreaMirror}
                />

                <div ref={this.props.textAreaMirrorContainerRef} className={"mirror-container"}>
                    <span ref={this.props.textAreaMirrorRef} />
                </div>
                <div ref={this.props.autoCompletePositioningRef} className={"ac-position"} />
            </div>
        );
    }

    public componentDidMount(): void {
        if (this._textArea) {
            // When the component is first mounted, it often hasn't actually been painted onto the screen yet and so any
            // immediate calculations of height will often return 0 and the control will collapse down to its min height despite
            // having content that should be taking up more space. And so we have to delay the calculation.
            setTimeout(() => {
                this._setEditHeight();
            }, 0);
        }
    }

    public componentDidUpdate(prevProps: IMarkdownTextAreaProps) {
        if (prevProps.value !== this.props.value) {
            this._setEditHeight();
        }
    }

    private _onTextChange = (event: React.FormEvent<HTMLTextAreaElement>): void => {
        if (this.props.onTextChange) {
            this.props.onTextChange((event.target as HTMLTextAreaElement).value);
        }
    };

    private _setTextArea = (textArea: HTMLTextAreaElement): void => {
        this._textArea = textArea;
        this._setEditHeight();

        if (this.props.textAreaRef) {
            this.props.textAreaRef(textArea);
        }
    };

    private _setTextAreaMirror = (textAreaMirror: HTMLTextAreaElement): void => {
        this._textAreaMirror = textAreaMirror;
    };

    private _setEditHeight() {
        if (this._textArea && this._textAreaMirror) {
            if (this._textAreaMirror.style.width !== `${this._textArea.clientWidth}px`) {
                this._textAreaMirror.style.width = `${this._textArea.clientWidth}px`;
            }

            const scrollHeight: number = this._textAreaMirror.scrollHeight;
            const maxHeight = this.props.maxHeight;
            const minHeight = this.props.minHeight;
            const visibleHeight = Math.min(Math.max(scrollHeight, minHeight!), maxHeight!);

            // only update the visible text area height if it actually changed - prevents reflow
            if (this._textArea.style.height !== `${visibleHeight}px`) {
                // hack for IE. If overflow is left to default, then a useless scrollbar shows up in IE
                // if overflow is set to auto, then the scrollbar flashes in IE with each height change
                // So, dynamically set the scrollbar visibility when we need it to be there
                this._textArea.style.overflowY = visibleHeight < maxHeight! ? "hidden" : "visible";
                this._textArea.style.height = `${visibleHeight}px`;
            }
        }
    }

    private _onPaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const { onAttachmentsAdded } = this.props;
        const data: DataTransfer = event.clipboardData;
        if (!data || !data.items || !onAttachmentsAdded) {
            return;
        }
        let fileIndex: number = -1;
        for (let i = 0; i < data.items.length; i += 1) {
            if (data.items[i].type.indexOf("image/") === 0) {
                fileIndex = i;
                break;
            }
        }

        if (fileIndex >= 0) {
            const file = data.items[fileIndex].getAsFile();
            if (file) {
                onAttachmentsAdded([file], this._textArea.selectionStart, this._textArea.selectionEnd);
            }
        }
    };
}
